// The huddle's words (BL-007): the pace headline as Brook's GP-tracker says it, the basis the revenue
// is counted on, and the goals form's fields to and from gp.setTarget.

import { addDays, dayText, money, monthName, parseMoney } from '../books/model.ts';
import { BOOKS_INCOME } from '../shared/revenue-words.ts';

export interface HuddleDay {
  date: string;
  working: boolean;
  closed: string | null;
  revenueCents: number;
  provisionalCents: number;
  inProgressCents: number;
  jobs: number;
  sold: number;
  workingDaysPassed: number;
  note: string | null;
  today: boolean;
  future: boolean;
}

export interface Huddle {
  from: string;
  to: string;
  today: string;
  basis: 'pre_tax' | 'with_tax';
  status: 'no_target' | 'too_early' | 'ahead' | 'behind';
  workingDays: number;
  workingDaysElapsed: number;
  goalMonths: { set: number; total: number };
  targetCents: number | null;
  expectedCents: number;
  actualCents: number;
  totalCents: number;
  deltaCents: number | null;
  deltaPct: number | null;
  projectedCents: number | null;
  provisionalCents: number;
  inProgressCents: number;
  recognisedCents: number | null;
  jobCount: number;
  targets: {
    revenueCents: number | null;
    plannedTechs: number | null;
    actualTechs: number;
    actualTechsFrom: 'timesheets' | 'set';
    callsPerTechDay: number | null;
    monthlyCalls: number | null;
  };
  budgeted: {
    dailyRevenueCents: number | null;
    dailyCalls: number | null;
    avgTicketCents: number | null;
    conversionPct: number | null;
    avgSaleCents: number | null;
  };
  days: HuddleDay[];
}

export const BASIS_WORDS = { pre_tax: 'Counted before tax', with_tax: 'Counted with tax' } as const;

/** Who sets the huddle's goals: whoever holds finance.targets.edit (gp.setTarget); who sees it is Nav's HUDDLE_PERMS. */
export const mayEditGoals = (held: readonly string[]) => held.includes('finance.targets.edit');

const pct = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(1)} %`;

/** The headline: the pace as a number and a word, and the line under it. */
export function paceWords(h: Huddle): {
  big: string | null;
  word: string;
  tone: 'ok' | 'bad' | 'quiet';
  line: string;
} {
  const month = monthName(h.from);
  if (h.status === 'no_target')
    return {
      big: null,
      word: 'No goal yet',
      tone: 'quiet',
      line: `There’s no revenue goal for ${month}, so there’s no pace.`,
    };
  if (h.status === 'too_early')
    return {
      big: null,
      word: 'Too early to read',
      tone: 'quiet',
      line: `No working day has finished yet${h.totalCents ? `; ${money(h.totalCents)} in so far` : ''}.`,
    };
  const through = h.to < h.today ? h.to : addDays(h.today, -1);
  return {
    big: pct(h.deltaPct ?? 0),
    word: h.status === 'ahead' ? 'ahead of pace' : 'behind pace',
    tone: h.status === 'ahead' ? 'ok' : 'bad',
    line: `${money(h.actualCents)} earned against ${money(h.expectedCents)} expected through ${dayText(through)} · working day ${h.workingDaysElapsed} of ${h.workingDays}`,
  };
}

/** The quieter lines under the headline, each only when it says something. */
export function paceNotes(h: Huddle): string[] {
  return paceNoteList(h).map((n) => n.text);
}

/**
 * The same lines by what each says, so Blueprint's pace (ui-web's GoalPace, which already says where the
 * month heads and what is in review) leaves out the two it draws.
 */
export function paceNoteList(
  h: Huddle,
): { key: 'months' | 'heading' | 'review' | 'open' | 'recognised'; text: string }[] {
  const out: ReturnType<typeof paceNoteList> = [];
  if (h.goalMonths.set > 0 && h.goalMonths.set < h.goalMonths.total)
    out.push({
      key: 'months',
      text: `The pace covers ${h.goalMonths.set} of ${h.goalMonths.total} months; the others have no goal.`,
    });
  if (h.projectedCents !== null && h.status !== 'no_target')
    out.push({ key: 'heading', text: `Heading for ${money(h.projectedCents)}.` });
  if (h.provisionalCents)
    out.push({
      key: 'review',
      text: `${money(h.provisionalCents)} of it is on jobs waiting on Review (draft invoices).`,
    });
  if (h.inProgressCents)
    out.push({
      key: 'open',
      text: `${money(h.inProgressCents)} more on open jobs’ finished visits, not counted until they’re done.`,
    });
  if (h.recognisedCents !== null)
    out.push({
      key: 'recognised',
      text: `${money(h.recognisedCents)} in the books’ income, before tax. ${BOOKS_INCOME}`,
    });
  return out;
}

export const COUNTING =
  'A job’s revenue is spread over the days it was worked. A credit note reduces its job’s revenue; payments and refunds don’t. Reports and Performance count the same revenue on each invoice’s and credit note’s own date instead, so a job worked in one month and invoiced in the next is in the next there.';

export type GoalField =
  | 'revenueCents'
  | 'plannedTechs'
  | 'callsPerTechDay'
  | 'conversionPct'
  | 'avgTicketCents'
  | 'actualTechsOverride';
export const GOAL_FIELDS: { key: GoalField; label: string; money?: boolean; hint?: string }[] = [
  { key: 'revenueCents', label: 'Revenue goal', money: true },
  { key: 'plannedTechs', label: 'Planned technicians' },
  { key: 'callsPerTechDay', label: 'Calls a technician a day' },
  { key: 'conversionPct', label: 'Conversion rate (%)' },
  { key: 'avgTicketCents', label: 'Average ticket', money: true },
  { key: 'actualTechsOverride', label: 'Technicians working', hint: 'Empty: counted from the timesheets' },
];

/** A goal as the form shows it. */
export function goalText(field: (typeof GOAL_FIELDS)[number], v: number | null | undefined): string {
  if (v == null) return '';
  return field.money ? (v / 100).toFixed(2).replace(/\.00$/, '') : String(v);
}

/** The form's text back to gp.setTarget's fields: empty clears; a word that isn't a number is refused by name. */
export function goalInput(
  texts: Record<GoalField, string>,
): { fields: Record<GoalField, number | null> } | { error: string } {
  const fields = {} as Record<GoalField, number | null>;
  for (const f of GOAL_FIELDS) {
    const t = texts[f.key].trim().replace(/[$,\s]/g, '');
    if (!t) {
      fields[f.key] = null;
      continue;
    }
    const n = f.money ? parseMoney(t) : /^\d+(\.\d+)?$/.test(t) ? Number(t) : null;
    if (n === null || n < 0) return { error: `${f.label} isn’t a number.` };
    if (f.key === 'conversionPct' && n > 100) return { error: 'The conversion rate is at most 100 %.' };
    fields[f.key] = n;
  }
  return { fields };
}

/** "Thu 15": a day of the table. */
export function dayLabel(date: string): { num: string; weekday: string } {
  const d = new Date(`${date}T12:00:00Z`);
  return {
    num: String(d.getUTCDate()),
    weekday: new Intl.DateTimeFormat('en-CA', { weekday: 'short', timeZone: 'UTC' }).format(d),
  };
}

/** The month before or after a month's first day. */
export function stepMonth(from: string, by: 1 | -1): [string, string] {
  const [y, m] = from.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  const first = d.toISOString().slice(0, 10);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
  return [first, last];
}

/** Blueprint's figures for the month (at most five): the pace, what's in, the goal, where it heads, the jobs. */
export function huddleFigures(h: Huddle): { label: string; value: string; sub?: string }[] {
  const w = paceWords(h);
  const cents = (v: number | null) => (v === null ? '—' : money(v));
  return [
    { label: 'Pace', value: w.big ?? w.word, ...(w.big ? { sub: w.word } : {}) },
    {
      // everything in so far, through today, goal or not (QA-683): the pace's own figure is through the
      // last finished day, over the months with a goal, and its line says so with its own date
      label: 'Earned so far',
      value: money(h.totalCents),
      ...(h.today >= h.from ? { sub: `to ${dayText(h.to < h.today ? h.to : h.today)}` } : {}),
    },
    {
      label: 'Revenue goal',
      value: cents(h.targetCents),
      sub: `working day ${h.workingDaysElapsed} of ${h.workingDays}`,
    },
    { label: 'Heading for', value: h.status === 'no_target' ? '—' : cents(h.projectedCents) },
    { label: 'Jobs earned', value: String(h.jobCount), sub: monthName(h.from) },
  ];
}
